import { Controller as BaseController } from 'stimulus'
import { getCurrentLocale } from '@app/javascript/locales'
import I18n from '@app/javascript/i18n/index.js.erb'
import castArray from 'lodash.castarray'
import Rails from '@rails/ujs'
import { flashError } from '@app/javascript/classes/flashMessages'

export class Controller extends BaseController {
  static targets = ['reportButton', 'reportLimitHint']

  static values = {
    reportRequestUrl: String,
    currentSearchParams: Object,
    reportLimit: Number,
    reportLimitFormatted: String,
    totalCount: Number,
    unsupportedContentTypes: Array,
    requestAction: String
  }

  connect() {
    this.reportButtonLabel = this.reportButtonTarget.querySelector('.button-label')
    this.selectedEntityIds = []
  }

  onSearchChanged(event) {
    this.isResultsLoaded = false
    this.totalCountValue = 0
    this.currentSearchParamsValue = event.detail.paramsObj
    this.setDisabled()
  }

  onResultsLoaded(event) {
    this.isResultsLoaded = true
    this.totalCountValue = event.detail.totalCount
    this.setEnabledDisabled()
  }

  onResultsSelected(event) {
    if (!this.isResultsLoaded) return

    const { selectedEntityIds } = event.detail

    this.selectedEntityIds = selectedEntityIds
    this.setEnabledDisabled()
  }

  setEnabledDisabled() {
    const isDisabled = this.totalToReport === 0 || this.isReportUnsupportedForCurrentSearch()

    isDisabled ? this.setDisabled() : this.setEnabled()
  }

  setEnabled() {
    if (this.totalToReport <= this.reportLimitValue) {
      this.setEnabledBelowLimit()
    } else {
      this.setEnabledBeyondLimit()
    }
  }

  setEnabledBelowLimit() {
    const formattedCount = new Intl.NumberFormat(getCurrentLocale()).format(this.totalToReport)

    this.reportButtonLabel.innerHTML = I18n.t('search_v2.policy_reports.button_label_enabled', { action: this.requestActionValue, count: formattedCount })
    this.reportButtonTarget.disabled = false

    this.hideReportLimitHint()
  }

  setEnabledBeyondLimit() {
    this.reportButtonLabel.innerHTML = I18n.t('search_v2.policy_reports.button_label_limit', { action: this.requestActionValue, report_limit: this.reportLimitFormattedValue })
    this.reportButtonTarget.disabled = false

    this.updateReportLimitHint('search_v2.policy_reports.button_hint', { action: this.requestActionValue, report_limit: this.reportLimitFormattedValue })
  }

  setDisabled() {
    this.reportButtonTarget.disabled = true

    if (this.totalToReport === 0) {
      this.hideReportLimitHint()
    } else {
      this.updateReportLimitHint('search_v2.policy_reports.button_hint_disabled', { action: this.requestActionValue })
    }
  }

  updateReportLimitHint(copyString, stringOptions = {}) {
    if (!this.hasReportLimitHintTarget) return

    const hintTargetController = this.reportLimitHintTarget.controllers['vendor--tippy']

    if (hintTargetController) {
      hintTargetController.optionsValue = { content: I18n.t(copyString, stringOptions) }
      hintTargetController.reset()
    }

    this.reportLimitHintTarget.classList.remove('hidden')
  }

  hideReportLimitHint() {
    if (!this.hasReportLimitHintTarget) return

    this.reportLimitHintTarget.classList.add('hidden')
  }

  createPolicyReport() {
    this.setDisabled()
    this.updateReportLimitHint('search_v2.policy_reports.button_hint_creating')
    let requestType = 'POST'
    if (this.requestActionValue === 'Update') {
      requestType = 'PUT'
    }

    Rails.ajax({
      type: requestType,
      url: this.reportRequestUrlValue,
      dataType: 'json',
      data: $.param({
        search_params: this.currentSearchParamsValue,
        selected_entity_ids: this.selectedEntityIds
      }),
      error: () => {
        flashError(I18n.t('common.something_went_wrong_try_again'))
        this.setEnabled()
      }
    })
  }

  isReportUnsupportedForCurrentSearch() {
    if (this.unsupportedContentTypesValue.length === 0) { return false }
    return castArray(this.currentSearchParamsValue.content_type).every(value => this.unsupportedContentTypesValue.includes(value))
  }

  get totalToReport() {
    const hasSelectedEntities = this.selectedEntityIds && this.selectedEntityIds.length > 0

    return hasSelectedEntities ? this.selectedEntityIds.length : this.totalCountValue
  }
}
